import type { PillTone } from '@concord/domain';
import { ageLabel, fmtDayShort, fmtTime } from '@concord/domain';
import { Masked, Pill } from '@concord/ui';
import { useNavigate } from 'react-router';
import s from './cases.module.css';

export interface CaseRowView {
  id: string;
  reference: string | null;
  patient: string;
  masked: boolean;
  requester: 'patient' | 'navigator';
  caseType: string | null;
  ageSex: string;
  customer: string | null;
  pool: string | null;
  state: string;
  stateLabel: string;
  tone: string;
  qualityReview: boolean;
  panel: { segments: { kind: string; count: number }[]; caption: string };
  consultation: { startsAt: string; state: string; durationMinutes: number } | null;
  timesOffered: number;
  openExpiresAt: string | null;
  attention: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export type Queue = 'attention' | 'release' | 'panel' | 'today' | 'week' | 'closure' | 'all' | 'closed';

export const QUEUE_CHIPS: { key: Queue; label: string; dot?: string }[] = [
  { key: 'attention', label: 'Needs attention' },
  { key: 'release', label: 'Awaiting release', dot: 'var(--alert)' },
  { key: 'panel', label: 'Awaiting panel', dot: 'var(--wait)' },
  { key: 'today', label: 'Scheduled today', dot: 'var(--ok)' },
  { key: 'week', label: 'Scheduled this week', dot: 'var(--ok)' },
  { key: 'closure', label: 'Awaiting closure', dot: 'var(--neutral-mark)' },
];

/** The case table from the dashboard prototype, used on the dashboard and the case list (CON-02). */
export function CaseTable({ rows, tz, now }: { rows: CaseRowView[]; tz: string; now: Date }) {
  const navigate = useNavigate();
  const open = (r: CaseRowView) => r.reference && navigate(`/console/cases/${r.reference}`);
  return (
    <div className="tw">
      <table className={s.table}>
        <thead>
          <tr>
            <th>Case</th>
            <th>Patient</th>
            <th>Case type</th>
            <th>Plan / network</th>
            <th>State</th>
            <th>Panel</th>
            <th>Consultation</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const since = new Date(r.submittedAt ?? r.createdAt);
            const age = ageLabel(since, now);
            const hot = now.getTime() - since.getTime() > 3 * 86_400_000 && r.attention !== null;
            const k = r.consultation;
            return (
              <tr
                key={r.id}
                tabIndex={0}
                onClick={() => open(r)}
                onKeyDown={(e) => e.key === 'Enter' && open(r)}
                aria-label={`${r.reference} ${r.masked ? 'masked patient' : r.patient}`}
              >
                <td className="ref">{r.reference}</td>
                <td className={`pt ${s.pt}`}>
                  {r.masked ? <Masked masked={r.patient} /> : r.patient}
                  {r.requester === 'navigator' && <div className="sub2">By phone</div>}
                  {r.qualityReview && <div className="sub2">Quality review</div>}
                </td>
                <td>
                  {r.caseType ?? '—'}
                  {r.ageSex && <div className="sub2">{r.ageSex}</div>}
                </td>
                <td>
                  {r.customer ?? '—'}
                  <div className="sub2">{r.pool}</div>
                </td>
                <td>
                  <Pill tone={r.tone as PillTone}>{r.stateLabel}</Pill>
                  {r.attention && <div className="sub2">{r.attention}</div>}
                </td>
                <td>
                  <div className={s.pbar}>
                    <div className={s.track}>
                      {r.panel.segments.length ? (
                        r.panel.segments.map((g) => (
                          <span key={g.kind} className={`${s.seg} ${s[g.kind]}`} style={{ flex: g.count }} />
                        ))
                      ) : (
                        <span className={`${s.seg} ${s.mis}`} style={{ flex: 1 }} />
                      )}
                    </div>
                    <span className={s.cap}>{r.panel.caption}</span>
                  </div>
                </td>
                <td className={s['when-c']}>
                  {k ? (
                    <>
                      {fmtDayShort(new Date(k.startsAt), tz)}
                      <div className="sub2">
                        {fmtTime(new Date(k.startsAt), tz)} ·{' '}
                        {k.state === 'provisional'
                          ? 'provisional'
                          : k.state === 'completed'
                            ? 'completed'
                            : 'confirmed'}
                      </div>
                    </>
                  ) : r.timesOffered ? (
                    <>
                      {r.timesOffered} {r.timesOffered === 1 ? 'time' : 'times'} offered
                      {r.openExpiresAt && (
                        <div className="sub2">
                          Expires in {ageLabel(now, new Date(r.openExpiresAt)).replace('m', ' min')}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      —<div className="sub2">No times offered</div>
                    </>
                  )}
                </td>
                <td className={`${s.age} ${hot ? s.hot : ''}`}>{age}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Legend() {
  return (
    <div className={s.legend}>
      <span>
        <i style={{ background: 'var(--ok)' }} />
        Accepted / attended
      </span>
      <span>
        <i style={{ background: 'var(--wait)' }} />
        Invited, awaiting reply
      </span>
      <span>
        <i style={{ background: 'var(--bad)' }} />
        Declined
      </span>
      <span>
        <i style={{ background: 'var(--neutral-mark)' }} />
        Missed out / none
      </span>
    </div>
  );
}
